import styled from "styled-components";

import { PaymentRecipientStatus } from "../../services/payments";
import { Segmented } from "../Domains/styles/detail";

export const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

export const Message = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;

export const ErrorMessage = styled(Message)`
  color: var(--color-feedback-danger);
`;

export const Fees = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/* The Domains' two-way choice, a step lighter than the card it sits on */
export const Choice = styled(Segmented)`
  background: var(--color-neutral-base);

  button[aria-checked="true"] {
    background: var(--color-neutral-dark);
  }
`;

/* One line: its text on the left, its action (pill button) on the right */
export const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-family: Inter, sans-serif;

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .title {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .description {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }
`;

/* The dashboard's numbers, side by side (one under the other on a phone) */
export const Amounts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 1rem;
  font-family: Inter, sans-serif;
`;

/* One of the dashboard's amounts: its name over its value, and a detail under it */
export const Amount = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-family: Inter, sans-serif;

  .label {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  /* a box's own name, as its title */
  .label.title {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .value {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.75rem;
    color: var(--color-neutral-white);
  }

  .detail {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }
`;

/* The review's status with its ● colored dot, like a domain's on /domains */
export const Status = styled.span<{ $status: PaymentRecipientStatus }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $status }) =>
    ({
      draft: "var(--color-neutral-150)",
      pending: "#ffd00d",
      needs_info: "#ffb066",
      approved: "var(--color-feedback-success)",
      refused: "#ff8aa0",
    })[$status]};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/* Two boxes side by side, one under the other on a phone */
export const Pair = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem;
`;

/* One line of a box: its name on the left, its value on the right */
export const Line = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);

  strong {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-align: right;
  }

  /* a name over its detail */
  .text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .name {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }
`;

/* A choice of two, one box each: the chosen one with the brand's border and its ● filled */
export const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem;
`;

export const Option = styled.button`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem;
  font-family: Inter, sans-serif;
  text-align: left;
  background: var(--color-neutral-dark);
  border: 1px solid transparent;
  border-radius: 16px;
  transition: border-color 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--color-neutral-150);
  }

  &[aria-checked="true"] {
    border-color: var(--color-brand-blue);
  }

  &:disabled {
    cursor: progress;
  }

  .dot {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    border: 2px solid var(--color-neutral-150);
    border-radius: 100%;
  }

  &[aria-checked="true"] .dot {
    border: 5px solid var(--color-brand-blue);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .title {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .description {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }
`;
